import React, { useState } from 'react';
import styled from 'styled-components';
import { Coins, ChevronDown, Users } from 'lucide-react';
import { CommissionsOverview } from '../../../services/dashboardService';

interface Props {
  data: CommissionsOverview;
  // כותרת מותאמת (לנציג: "העמלות שלי"); ברירת מחדל — תצוגת מנהל
  title?: string;
  // האם להציג עמודת שם נציג (במנהל/מפקח כן; בנציג בודד אפשר להסתיר)
  showRepNames?: boolean;
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const TotalCard = styled.div`
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TotalLabel = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const TotalValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: var(--text-primary);
  direction: ltr;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const RepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RepBlock = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
`;

const RepHeader = styled.button<{ $expanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: ${props => (props.$expanded ? 'var(--background-color)' : 'white')};
  border: none;
  cursor: pointer;
  text-align: right;
  direction: rtl;

  &:hover {
    background: var(--background-color);
  }
`;

const RepName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
`;

const RepRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const RepTotal = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--success-color);
  direction: ltr;
  filter: saturate(0.7) brightness(0.9);
`;

const Chevron = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  transition: transform 0.2s ease;
  transform: rotate(${props => (props.$expanded ? '180deg' : '0deg')});
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  direction: rtl;
`;

const Th = styled.th`
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  padding: 10px 18px;
  background: var(--background-color);
  border-top: 1px solid var(--border-color);
  white-space: nowrap;
`;

const Td = styled.td`
  font-size: 13px;
  color: var(--text-primary);
  padding: 10px 18px;
  border-top: 1px solid var(--border-color);
  white-space: nowrap;
`;

const NumCell = styled(Td)`
  direction: ltr;
  text-align: right;
`;

const CommissionCell = styled(NumCell)`
  font-weight: 600;
  color: var(--success-color);
  filter: saturate(0.7) brightness(0.9);
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  padding: 28px 0;
`;

const formatCurrency = (amount: number): string => {
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const AdminCommissionsOverview: React.FC<Props> = ({ data, title = 'עמלות נציגי מכירות', showRepNames = true }) => {
  // נציג בודד — פתוח כברירת מחדל; ריבוי נציגים — מכווץ
  const singleRep = data.reps.length === 1;
  const [expanded, setExpanded] = useState<Record<number, boolean>>(
    singleRep ? { [data.reps[0].salesRepId]: true } : {}
  );

  const toggle = (repId: number) => {
    setExpanded(prev => ({ ...prev, [repId]: !prev[repId] }));
  };

  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <Coins size={20} />
        </SectionIcon>
        <SectionTitle>{title}</SectionTitle>
      </SectionHeader>

      <TotalCard>
        <TotalLabel>סך כל העמלות</TotalLabel>
        <TotalValue>{formatCurrency(data.totalCommissions)}</TotalValue>
      </TotalCard>

      {data.reps.length === 0 ? (
        <EmptyState>אין עדיין עמלות להצגה (לקוחות מאושרים עם חיוב)</EmptyState>
      ) : (
        <RepList>
          {data.reps.map(rep => {
            const isExpanded = !!expanded[rep.salesRepId];
            return (
              <RepBlock key={rep.salesRepId}>
                <RepHeader $expanded={isExpanded} onClick={() => toggle(rep.salesRepId)} type="button">
                  <RepName>
                    {showRepNames && <Users size={16} />}
                    {showRepNames ? rep.salesRepName : 'פילוח לפי לקוח'}
                  </RepName>
                  <RepRight>
                    <RepTotal>{formatCurrency(rep.totalCommission)}</RepTotal>
                    <Chevron $expanded={isExpanded}>
                      <ChevronDown size={18} />
                    </Chevron>
                  </RepRight>
                </RepHeader>

                {isExpanded && (
                  <TableWrapper>
                    <Table>
                      <thead>
                        <tr>
                          <Th>לקוח</Th>
                          <Th>סכום עסקה</Th>
                          <Th>אחוז</Th>
                          <Th>קבוע</Th>
                          <Th>עמלה</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {rep.clients.map(client => (
                          <tr key={client.clientId}>
                            <Td>{client.clientName}</Td>
                            <NumCell>{formatCurrency(client.totalCharge)}</NumCell>
                            <NumCell>{client.commissionPercentage}%</NumCell>
                            <NumCell>{formatCurrency(client.commissionFixedAmount)}</NumCell>
                            <CommissionCell>{formatCurrency(client.commission)}</CommissionCell>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrapper>
                )}
              </RepBlock>
            );
          })}
        </RepList>
      )}
    </Container>
  );
};

export default AdminCommissionsOverview;
